import { useCallback, useEffect } from 'react';
import { useLazyQuery, useMutation, ApolloError } from '@apollo/client';
import {
  CREATE_INVITATION,
  DELETE_INVITATION,
  GET_INVITATIONS,
  RESEND_INVITATION,
} from '@bloomays-lib/adapter.api-talent';
import { GqlTalentBoardInvitation } from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useInvitations');

type GetInvitationsResponse = {
  getInvitations: GqlTalentBoardInvitation[];
};

type CreateInvitationVariables = {
  jobId: string;
  email: string;
  message?: string;
};

type DeleteInvitationVariables = {
  id: string;
};

type ResendInvitationVariables = {
  id: string;
};

export const useInvitations = (jobId?: string) => {
  const refetchConfig = {
    refetchQueries: [{ query: GET_INVITATIONS, variables: { jobId }, context: { clientName: 'api.talents' } }],
    awaitRefetchQueries: true,
  };

  const [fetchInvitations, { data, loading: fetchLoading, error: fetchError }] = useLazyQuery<GetInvitationsResponse>(
    GET_INVITATIONS,
    {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [createInvitationMutation, { loading: createLoading, error: createError }] = useMutation<
    { createInvitation: GqlTalentBoardInvitation },
    CreateInvitationVariables
  >(CREATE_INVITATION, {
    context: { clientName: 'api.talents' },
    ...refetchConfig,
  });

  const [deleteInvitationMutation, { loading: deleteLoading, error: deleteError }] = useMutation<
    { deleteInvitation: boolean },
    DeleteInvitationVariables
  >(DELETE_INVITATION, {
    context: { clientName: 'api.talents' },
    ...refetchConfig,
  });

  const [resendInvitationMutation, { loading: resendLoading, error: resendError }] = useMutation<
    { resendInvitation: GqlTalentBoardInvitation },
    ResendInvitationVariables
  >(RESEND_INVITATION, {
    context: { clientName: 'api.talents' },
    ...refetchConfig,
  });

  useEffect(() => {
    if (!jobId) {
      return;
    }
    logger.debug('Fetching invitations', { jobId });
    void fetchInvitations({ variables: { jobId } });
  }, [jobId, fetchInvitations]);

  useEffect(() => {
    if (fetchError && jobId) {
      logger.error('Error fetching invitations', { jobId }, fetchError);
      monitorException(fetchError, { name: 'useInvitations', jobId });
    }
  }, [fetchError, jobId]);

  const createInvitation = useCallback(
    async (variables: CreateInvitationVariables): Promise<GqlTalentBoardInvitation | null> => {
      try {
        const result = await createInvitationMutation({ variables });
        return result.data?.createInvitation ?? null;
      } catch (err) {
        logger.error('Error creating invitation', variables, err as Error);
        monitorException(err as Error, { name: 'useInvitations.createInvitation', variables });
        throw err;
      }
    },
    [createInvitationMutation],
  );

  const deleteInvitation = useCallback(
    async (variables: DeleteInvitationVariables): Promise<boolean> => {
      try {
        const result = await deleteInvitationMutation({ variables });
        return result.data?.deleteInvitation ?? false;
      } catch (err) {
        logger.error('Error deleting invitation', variables, err as Error);
        monitorException(err as Error, { name: 'useInvitations.deleteInvitation', variables });
        return false;
      }
    },
    [deleteInvitationMutation],
  );

  const resendInvitation = useCallback(
    async (variables: ResendInvitationVariables): Promise<GqlTalentBoardInvitation | null> => {
      try {
        const result = await resendInvitationMutation({ variables });
        return result.data?.resendInvitation ?? null;
      } catch (err) {
        logger.error('Error resending invitation', variables, err as Error);
        monitorException(err as Error, { name: 'useInvitations.resendInvitation', variables });
        return null;
      }
    },
    [resendInvitationMutation],
  );

  const error: ApolloError | undefined = fetchError || createError || deleteError || resendError;

  return {
    invitations: data?.getInvitations ?? [],
    loading: fetchLoading || !jobId,
    error,
    createInvitation,
    isCreating: createLoading,
    deleteInvitation,
    isDeleting: deleteLoading,
    resendInvitation,
    isResending: resendLoading,
  };
};

export default useInvitations;
